import { Tabs, TabsContent, TabsList, TabsTrigger } from '@design-system';
import {
  EmailTab,
  LinkTab,
  FillIntakeFormTab,
  useInviteClientTabs,
  useInviteClientTrigger,
  type TabType,
} from '@/features/invite-client';
import { PilotRestricted } from '@/shared/components/pilot-restricted';

import { ClipboardPen, Link2, Mail } from 'lucide-react';

/**
 * `TabsTrigger` is `whitespace-nowrap` with padding, so a full strip cannot shrink below the sum
 * of its tabs and the DIALOG gives instead, clipping its own body text. `min-w-0` plus `truncate`
 * is the guard that stops a fifth tab or a longer translation breaking the layout again.
 */
const TRIGGER_CLASS = 'min-w-0 flex-1 gap-2';
const RESTRICTED_TRIGGER_CLASS = 'min-w-0 flex-1 [&>div]:min-w-0 [&>div]:w-full';

export function InviteClientTabs() {
  const { setInviteClientTab } = useInviteClientTrigger();
  const {
    resolvedTab,
    isEmailTabVisible,
    isPrefillTabAvailable,
    hasAgentEnteredIntake,
    showFillFormTab,
    isLinkOnly,
  } = useInviteClientTabs();

  if (isLinkOnly) {
    return (
      <div className="mt-4 w-full">
        <LinkTab withHeader={false} />
      </div>
    );
  }

  return (
    <Tabs
      value={resolvedTab}
      onValueChange={value => setInviteClientTab(value as TabType)}
      className="w-full min-w-0"
    >
      <TabsList className="w-full min-w-0 overflow-hidden">
        {isEmailTabVisible && (
          <PilotRestricted
            capability="invite-client"
            className={RESTRICTED_TRIGGER_CLASS}
          >
            <TabsTrigger
              value="email"
              className={TRIGGER_CLASS}
            >
              <Mail className="hidden size-4 sm:block" />
              <span className="truncate">Invite via Email</span>
            </TabsTrigger>
          </PilotRestricted>
        )}
        {isPrefillTabAvailable && (
          <TabsTrigger
            value="prefill"
            className={TRIGGER_CLASS}
          >
            <Mail className="hidden size-4 sm:block" />
            <span className="truncate">Prefillable Invite Link</span>
          </TabsTrigger>
        )}
        <TabsTrigger
          value="link"
          className={TRIGGER_CLASS}
        >
          <Link2 className="hidden size-4 sm:block" />
          <span className="truncate">
            {isPrefillTabAvailable ? 'Reusable Invite Link' : 'Share via link'}
          </span>
        </TabsTrigger>
        {showFillFormTab &&
          (hasAgentEnteredIntake ? (
            // The capability stays restricted; the consent flow is what re-opens this surface,
            // so wrapping it in PilotRestricted here would hide the very tab it re-opens.
            <TabsTrigger
              value="fill-form"
              className={TRIGGER_CLASS}
            >
              <ClipboardPen className="hidden size-4 sm:block" />
              <span className="truncate">Fill Intake Form</span>
            </TabsTrigger>
          ) : (
            <PilotRestricted
              capability="intake-form"
              className={RESTRICTED_TRIGGER_CLASS}
            >
              <TabsTrigger
                value="fill-form"
                className={TRIGGER_CLASS}
              >
                <ClipboardPen className="hidden size-4 sm:block" />
                <span className="truncate">Fill Intake Form</span>
              </TabsTrigger>
            </PilotRestricted>
          ))}
      </TabsList>
      {isEmailTabVisible && (
        <TabsContent
          value="email"
          className="mt-4"
        >
          <EmailTab deliveryMode="email" />
        </TabsContent>
      )}
      {isPrefillTabAvailable && (
        <TabsContent
          value="prefill"
          className="mt-4"
        >
          <EmailTab deliveryMode="link" />
        </TabsContent>
      )}
      <TabsContent
        value="link"
        className="mt-4"
      >
        <LinkTab />
      </TabsContent>
      {showFillFormTab && (
        <TabsContent
          value="fill-form"
          className="mt-4"
        >
          <FillIntakeFormTab />
        </TabsContent>
      )}
    </Tabs>
  );
}
